import React, { useState, useEffect } from "react";
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer } from "recharts";
import {
  ArrowUpRight,
  ArrowRightLeft,
  Share2,
  Edit,
  Trash2,
  X,
} from "lucide-react";
import { BASE_URL } from "utils/constant";
import { ProjectApi } from "api/projects/service";
import { MaterialsApi } from "api/materials/service";
import StockUpdateForm from "./StockUpdateforTask";
import { useAlert } from "components/UI_components/Alert";
import EllipsisMenu from "components/UI_components/EllipsisMenu";

const InventoryItemDetails = ({
  item: initialItem,
  onBack,
  onMaterialDeleted,
  userPermissions,
}) => {
  const alert = useAlert();
  const [item, setItem] = useState(initialItem);
  const [stockData, setStockData] = useState([]);
  const [showStockForm, setShowStockForm] = useState(false);
  const [stockSummary, setStockSummary] = useState({
    positive: 0,
    negative: 0,
    inStock: 0,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingStock, setEditingStock] = useState(null);
  const [refresh, setRefresh] = useState(false);
  const [isDeletingMaterial, setIsDeletingMaterial] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [transferSites, setTransferSites] = useState([]);
  const [isLoadingTransferSites, setIsLoadingTransferSites] = useState(false);
  const [isTransferringMaterial, setIsTransferringMaterial] = useState(false);
  const [transferForm, setTransferForm] = useState({
    destinationSiteId: "",
    quantity: "",
  });

  const sourceSiteValue =
    item?.sites ||
    item?.site_id ||
    (typeof item?.site === "object" ? item.site?.id : item?.site) ||
    localStorage.getItem("site");
  const sourceSiteId = String(sourceSiteValue || "");

  const calculateStockSummary = (data) => {
    const summary = data.reduce(
      (acc, curr) => {
        if (curr.entered_quantity > 0) {
          acc.positive += curr.entered_quantity;
        } else {
          acc.negative += Math.abs(curr.entered_quantity);
        }
        return acc;
      },
      { positive: 0, negative: 0 },
    );

    summary.inStock = summary.positive - summary.negative;
    return summary;
  };

  useEffect(() => {
    const fetchStockData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const accessToken = localStorage.getItem("access");
        const response = await fetch(
          `${BASE_URL}/inventory/get-stocks-of-a-material/`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${accessToken.trim()}`,
            },
            body: JSON.stringify({
              material: item.id,
            }),
          },
        );

        if (!response.ok) {
          throw new Error("Failed to fetch stock data");
        }

        const data = await response.json();
        setStockData(data.data);
        const summary = calculateStockSummary(data.data);
        setStockSummary(summary);
      } catch (error) {
        console.error("Error fetching stock data:", error);
        setError(error.message);
      } finally {
        setIsLoading(false);
      }
    };
    const fetchMaterial = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const accessToken = localStorage.getItem("access");
        const response = await fetch(
          `${BASE_URL}/inventory/get-a-material/${item.id}/`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${accessToken.trim()}`,
            },
          },
        );
        if (!response.ok) {
          throw new Error("Failed to fetch material data");
        }
        const data = await response.json();
        setItem(data.data);
      } catch (error) {
        console.error("Error fetching material data:", error);
        setError(error.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchMaterial();
    fetchStockData();
  }, [item.id, refresh]);

  useEffect(() => {
    if (!showTransferModal) return;

    const fetchTransferSites = async () => {
      setIsLoadingTransferSites(true);
      try {
        const organizationId = JSON.parse(
          localStorage.getItem("organization") || "{}",
        )?.id;
        const response = await ProjectApi.listSitesByUser();
        const sites = Array.isArray(response?.data)
          ? response.data
          : Array.isArray(response)
            ? response
            : [];

        const availableSites = sites.filter((site) => {
          const isSameSite = String(site.id) === sourceSiteId;
          const isSameOrg =
            !organizationId || String(site.organization) === String(organizationId);
          return !isSameSite && isSameOrg;
        });

        setTransferSites(availableSites);
      } catch (error) {
        console.error("Error fetching projects:", error);
        alert.addAlert(
          `Error loading projects: ${error.message}`,
          "error",
        );
      } finally {
        setIsLoadingTransferSites(false);
      }
    };

    fetchTransferSites();
  }, [alert, showTransferModal, sourceSiteId]);

  const handleStockUpdate = (newStockData) => {
    // Add the new stock data to the beginning of the array
    const updatedStockData = [newStockData, ...stockData];
    setStockData(updatedStockData);

    // Recalculate the summary
    const newSummary = calculateStockSummary(updatedStockData);
    setStockSummary(newSummary);
  };

  const handleEditStock = (stock) => {
    if (
      !userPermissions?.edit_material_management &&
      !userPermissions?.edit_all &&
      !userPermissions?.is_admin
    ) {
      alert.addAlert("You don't have permission to edit stock", "error");
      return;
    }
    setEditingStock(stock);
    setShowStockForm(true);
  };

  const handleDeleteStock = (stockId) => {
    if (
      !userPermissions?.edit_material_management &&
      !userPermissions?.edit_all &&
      !userPermissions?.is_admin
    ) {
      alert.addAlert("You don't have permission to delete stock", "error");
      return;
    }

    alert.addAlert(
      "Are you sure you want to delete this stock update?",
      "confirm",
      {
        onConfirm: async () => {
          try {
            const accessToken = localStorage.getItem("access");
            const response = await fetch(
              `${BASE_URL}/inventory/delete-stock/${stockId}/`,
              {
                method: "DELETE",
                headers: {
                  Authorization: `Bearer ${accessToken.trim()}`,
                },
              },
            );

            if (!response.ok) {
              throw new Error("Failed to delete stock");
            }

            const updatedStockData = stockData.filter(
              (stock) => stock.id !== stockId,
            );
            setStockData(updatedStockData);

            const newSummary = calculateStockSummary(updatedStockData);
            setStockSummary(newSummary);

            alert.addAlert("Stock deleted successfully", "success");
          } catch (error) {
            console.error("Error deleting stock:", error);
            alert.addAlert(`Error deleting stock: ${error.message}`, "error");
          }
        },
      },
    );
  };

  const handleUpdateStockSubmit = async (updatedStockData) => {
    try {
      const accessToken = localStorage.getItem("access");
      const response = await fetch(
        `${BASE_URL}/inventory/update-stock/${editingStock.id}/`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken.trim()}`,
          },
          body: JSON.stringify({
            updatedStockData,
          }),
        },
      );

      if (!response.ok) {
        throw new Error("Failed to update stock");
      }

      // Update the stock data in the state
      const updatedStockList = stockData.map((stock) =>
        stock.id === editingStock.id
          ? { ...stock, ...updatedStockData }
          : stock,
      );

      setStockData(updatedStockList);

      // Recalculate the summary
      const newSummary = calculateStockSummary(updatedStockList);
      setStockSummary(newSummary);

      setShowStockForm(false);
      setEditingStock(null);
      alert.addAlert("Stock updated successfully", "success");
    } catch (error) {
      console.error("Error updating stock:", error);
      alert.addAlert(`Error updating stock: ${error.message}`, "error");
    }
  };

  const handleDeleteMaterial = () => {
    if (
      !userPermissions?.edit_material_management &&
      !userPermissions?.edit_all &&
      !userPermissions?.is_admin
    ) {
      alert.addAlert("You don't have permission to delete material", "error");
      return;
    }

    alert.addAlert(
      "Are you sure you want to delete this material? This action cannot be undone.",
      "confirm",
      {
        onConfirm: async () => {
          try {
            setIsDeletingMaterial(true);
            const accessToken = localStorage.getItem("access");
            const response = await fetch(
              `${BASE_URL}/inventory/delete-material/${item.id}/`,
              {
                method: "DELETE",
                headers: {
                  Authorization: `Bearer ${accessToken.trim()}`,
                },
              },
            );
            if (!response.ok) {
              throw new Error("Failed to delete material");
            }
            alert.addAlert("Material deleted successfully", "success");
            if (onMaterialDeleted) {
              await onMaterialDeleted(item.id);
            }
            onBack?.();
          } catch (error) {
            console.error("Error deleting material:", error);
            alert.addAlert(`Error deleting material: ${error.message}`, "error");
          } finally {
            setIsDeletingMaterial(false);
          }
        },
      },
    );
  };

  const handleOpenTransferModal = () => {
    if (
      !userPermissions?.edit_material_management &&
      !userPermissions?.edit_all &&
      !userPermissions?.is_admin
    ) {
      alert.addAlert("You don't have permission to transfer material", "error");
      return;
    }

    if (stockSummary.inStock <= 0) {
      alert.addAlert("No stock available to transfer", "error");
      return;
    }

    setTransferForm({
      destinationSiteId: "",
      quantity: "",
    });
    setShowTransferModal(true);
  };

  const handleTransferMaterial = async (event) => {
    event.preventDefault();

    const organizationId = JSON.parse(
      localStorage.getItem("organization") || "{}",
    )?.id;
    const quantity = Number(transferForm.quantity);

    if (!organizationId) {
      alert.addAlert("Organization is missing", "error");
      return;
    }

    if (!sourceSiteId) {
      alert.addAlert("Source project is missing", "error");
      return;
    }

    if (!transferForm.destinationSiteId) {
      alert.addAlert("Please select a destination project", "error");
      return;
    }

    if (!quantity || quantity <= 0) {
      alert.addAlert("Please enter a valid transfer quantity", "error");
      return;
    }

    if (quantity > stockSummary.inStock) {
      alert.addAlert("Transfer quantity cannot exceed current stock", "error");
      return;
    }

    try {
      setIsTransferringMaterial(true);
      await MaterialsApi.transferMaterialToSite({
        organization: organizationId,
        source_site_id: Number(sourceSiteId),
        destination_site_id: Number(transferForm.destinationSiteId),
        source_material_id: item.id,
        quantity,
      });

      alert.addAlert("Material transferred successfully", "success");
      setShowTransferModal(false);
      setRefresh((prev) => !prev);
    } catch (error) {
      console.error("Error transferring material:", error);
      alert.addAlert(
        `Error transferring material: ${error.message}`,
        "error",
      );
    } finally {
      setIsTransferringMaterial(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString("en-US", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Prepare continuous (per-update) stock data for the chart.
  // 1. Sort updates chronologically.
  // 2. Build a cumulative stock level per update.
  // 3. Clamp at 0 so it never goes negative.
  // 4. Draw segments between consecutive updates, coloring them:
  //    - green when the next update adds stock
  //    - red when the next update removes stock
  const sortedStockData = [...stockData].sort(
    (a, b) =>
      new Date(a.last_updated_on || a.update_date) -
      new Date(b.last_updated_on || b.update_date),
  );

  let runningStock = 0;
  const cumulativeChartPoints = sortedStockData.map((stock, index) => {
    runningStock += stock.entered_quantity;
    if (runningStock < 0) runningStock = 0;

    return {
      index: index + 1, // Update 1, Update 2, ...
      date: stock.update_date,
      stockLevel: runningStock,
      change: stock.entered_quantity,
    };
  });

  // Add an explicit baseline so the first update renders as a segment.
  const chartData =
    cumulativeChartPoints.length > 0
      ? [{ index: 0, stockLevel: 0, change: 0 }, ...cumulativeChartPoints]
      : [];

  const xTicks = chartData.map((p) => p.index);
  const xDomainMax = xTicks.length ? xTicks[xTicks.length - 1] : 1;

  // Build small segments between each consecutive point with color
  // determined by whether the next update adds or removes stock.
  const stockSegments = [];
  for (let i = 0; i < chartData.length - 1; i++) {
    const current = chartData[i];
    const next = chartData[i + 1];
    const isGain = next.change >= 0;

    stockSegments.push({
      id: `${current.index}-${next.index}`,
      color: isGain ? "#10B981" : "#EF4444", // green for gain, red for fall
      data: [
        { index: current.index, stockLevel: current.stockLevel },
        { index: next.index, stockLevel: next.stockLevel },
      ],
    });
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-700"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen">
        <div className="text-red-600 mb-4">Error: {error}</div>
        <button onClick={onBack} className="text-blue-600 hover:text-blue-800">
          ← Back
        </button>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-100 w-full">
      <button
        onClick={onBack}
        className="mb-4 text-blue-600 hover:text-blue-800"
      >
        ← Back
      </button>

      {/* Main Container with Material Details, Stock Details, and Graph */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Left Section: Material Details and Stock Summary */}
        <div className="bg-white rounded-lg shadow p-4">
          {/* Material Details */}
          <div className="flex justify-between items-start gap-4 border-b border-gray-200 pb-4 mb-4">
            <div className="min-w-0 flex-1">
              <h1 className="text-xl font-semibold mb-2">
                {item.material_name || "Material Name"}
              </h1>
              <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-gray-600">
                <span>
                  Sub Category:{" "}
                  <span className="text-gray-900">
                    {item.subcategory || "N/A"}
                  </span>
                </span>
                <span>
                  Specification:{" "}
                  <span className="text-black">
                    {item.specification || "N/A"}
                  </span>
                </span>
                <span>
                  Brand Name:{" "}
                  <span className="text-black">
                    {item.brand_name || item.brand || "N/A"}
                  </span>
                </span>
              </div>
            </div>
            <div className="shrink-0">
              <EllipsisMenu
                items={[
                  {
                    key: "delete-material",
                    label: isDeletingMaterial ? "Deleting..." : "Delete",
                    icon: Trash2,
                    danger: true,
                    disabled: isDeletingMaterial,
                    onClick: handleDeleteMaterial,
                  },
                ]}
                buttonAriaLabel="Material actions"
              />
            </div>
          </div>

          {/* Stock Details Section */}
          <h3 className="text-sm font-medium mb-4 mt-6">Stock Details</h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="flex items-center gap-2 p-2 border rounded-lg hover:bg-gray-50">
              <div className="w-4 h-4 bg-purple-100 rounded-lg flex items-center justify-center">
                <Share2 className="w-3 h-3 text-purple-600" />
              </div>
              <span className="text-gray-600 text-sm">Current Stock:</span>
              <span className="font-medium text-sm">
                {stockSummary.inStock.toFixed(2)} {item?.uom}
              </span>
            </div>
            <div className="flex items-center gap-2 p-2 border rounded-lg hover:bg-gray-50">
              <div className="w-4 h-4 bg-green-100 rounded-lg flex items-center justify-center">
                <ArrowUpRight className="w-3 h-3 text-green-600" />
              </div>
              <span className="text-gray-600 text-sm">Added:</span>
              <span className="font-medium text-sm">
                {stockSummary.positive.toFixed(2)} {item?.uom}
              </span>
            </div>
            <div className="flex items-center gap-2 p-2 border rounded-lg hover:bg-gray-50">
              <div className="w-4 h-4 bg-red-100 rounded-lg flex items-center justify-center">
                <ArrowUpRight className="w-3 h-3 text-red-600 rotate-90" />
              </div>
              <span className="text-gray-600 text-sm">Used:</span>
              <span className="font-medium text-sm">
                {stockSummary.negative.toFixed(2)} {item?.uom}
              </span>
            </div>
          </div>
        </div>

        {/* Right Section: Stock Updates Graph */}
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex justify-between items-center mb-4">
            <button className="text-gray-600 border-b-2 border-gray-900 pb-1">
              Stock Updates
            </button>
          </div>
          <h3 className="text-sm font-medium mb-4">Change in stock</h3>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={chartData}>
              <XAxis
                type="number"
                dataKey="index"
                domain={[0, xDomainMax]}
                ticks={xTicks}
                allowDataOverflow
                tickFormatter={(value) =>
                  value === 0 ? "" : `Update ${value}`
                }
              />
              <YAxis />
              {/* Invisible base line so axes know the full domain */}
              <Line
                type="monotone"
                dataKey="stockLevel"
                stroke="transparent"
                dot={false}
                isAnimationActive={false}
              />
              {/* Colored continuous segments between days */}
              {stockSegments.map((segment) => (
                <Line
                  key={segment.id}
                  type="monotone"
                  data={segment.data}
                  dataKey="stockLevel"
                  stroke={segment.color}
                  strokeWidth={2}
                  dot={false}
                  isAnimationActive={false}
                  legendType="none"
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Stock Update History Section */}
      <div className="bg-white rounded-lg shadow p-4 mt-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium">Stock Update History</h3>
          <div className="flex items-center gap-3 ml-auto mr-10">
            <button
              className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-gray-200 transition-colors disabled:cursor-not-allowed disabled:opacity-60"
              onClick={handleOpenTransferModal}
              disabled={stockSummary.inStock <= 0 || isTransferringMaterial}
            >
              <ArrowRightLeft className="w-4 h-4" />
              Transfer Material
            </button>
            <button
              className="bg-purple-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 pr-2 hover:bg-purple-800 transition-colors"
              onClick={() => {
                if (
                  !userPermissions?.edit_material_management &&
                  !userPermissions?.edit_all &&
                  !userPermissions?.is_admin
                ) {
                  alert.addAlert(
                    "You don't have permission to update stock",
                    "error",
                  );
                  return;
                }
                setEditingStock(null);
                setShowStockForm(true);
              }}
            >
              <ArrowUpRight className="w-4 h-4" />
              Update Stock
            </button>
          </div>
        </div>
        <div className="space-y-4">
          {stockData.map((item) => (
            <div
              key={item.id}
              className="grid grid-cols-[auto,1fr,auto] gap-4 items-center border-b pb-3 hover:bg-gray-50"
            >
              <div className="flex items-center gap-3">
                <div
                  className={`w-6 h-6 flex items-center justify-center rounded-lg ${item.entered_quantity > 0 ? "bg-green-100" : "bg-red-100"
                    }`}
                >
                  <ArrowUpRight
                    className={`w-4 h-4 ${item.entered_quantity > 0
                      ? "text-green-600"
                      : "text-red-600 rotate-90"
                      }`}
                  />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span
                      className={`font-medium ${item.entered_quantity > 0
                        ? "text-green-600"
                        : "text-red-600"
                        }`}
                    >
                      {item.entered_quantity > 0 ? "+" : ""}
                      {Math.abs(item.entered_quantity)} {item?.uom}
                    </span>
                    <span className="text-sm text-gray-500">
                      {item.entered_quantity > 0 ? "Added" : "Used"}
                    </span>
                  </div>
                  <div className="text-sm text-gray-500">
                    {formatDate(item.update_date) +
                      " " +
                      formatDateTime(item?.last_updated_on)}
                  </div>
                </div>
              </div>
              <div className="text-sm text-gray-500">
                {item.comment
                  ? `Comment: ${item.comment}`
                  : "No comment provided"}
              </div>
              <div className="flex items-center gap-3">
                {item.created_by && (
                  <div className="text-sm text-gray-500 mr-2">
                    Last updated by: {item.last_updated_by}
                  </div>
                )}
                <EllipsisMenu
                  items={[
                    {
                      key: "edit-stock-update",
                      label: "Edit",
                      icon: Edit,
                      onClick: () => handleEditStock(item),
                    },
                    { type: "separator" },
                    {
                      key: "delete-stock-update",
                      label: "Delete",
                      icon: Trash2,
                      danger: true,
                      onClick: () => handleDeleteStock(item.id),
                    },
                  ]}
                  buttonAriaLabel="Stock actions"
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Stock Update Modal */}
      {showStockForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <StockUpdateForm
            onClose={() => {
              setShowStockForm(false);
              setEditingStock(null);
              setRefresh(!refresh);
            }}
            materialId={item.id}
            currentStock={stockSummary.inStock}
            onStockUpdate={
              editingStock ? handleUpdateStockSubmit : handleStockUpdate
            }
            materialDetails={item}
            editMode={!!editingStock}
            stockData={editingStock || {}}
          />
        </div>
      )}

      {/* Transfer Material Modal */}
      {showTransferModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4">
          <form
            onSubmit={handleTransferMaterial}
            className="bg-white rounded-lg shadow-xl w-full max-w-md"
          >
            <div className="flex items-center justify-between border-b px-5 py-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Transfer Material
                </h3>
                <p className="text-sm text-gray-500">
                  Available: {stockSummary.inStock.toFixed(2)} {item?.uom}
                </p>
              </div>
              <button
                type="button"
                className="p-1 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                onClick={() => setShowTransferModal(false)}
                aria-label="Close transfer modal"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4 px-5 py-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Destination Project
                </label>
                <select
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-600 disabled:bg-gray-100"
                  value={transferForm.destinationSiteId}
                  onChange={(event) =>
                    setTransferForm((prev) => ({
                      ...prev,
                      destinationSiteId: event.target.value,
                    }))
                  }
                  disabled={isLoadingTransferSites || isTransferringMaterial}
                  required
                >
                  <option value="">
                    {isLoadingTransferSites
                      ? "Loading projects..."
                      : "Select project"}
                  </option>
                  {transferSites.map((site) => (
                    <option key={site.id} value={site.id}>
                      {site.site_name || site.name || `Project ${site.id}`}
                    </option>
                  ))}
                </select>
                {!isLoadingTransferSites && transferSites.length === 0 && (
                  <p className="text-sm text-red-600 mt-1">
                    No other projects are available for transfer.
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Quantity
                </label>
                <div className="relative">
                  <input
                    type="number"
                    min="0"
                    max={stockSummary.inStock}
                    step="any"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 pr-16 text-sm focus:outline-none focus:ring-2 focus:ring-purple-600 disabled:bg-gray-100"
                    value={transferForm.quantity}
                    onChange={(event) => {
                      const nextValue = event.target.value;
                      const nextQuantity = Number(nextValue);
                      const cappedValue =
                        nextQuantity > stockSummary.inStock
                          ? String(stockSummary.inStock)
                          : nextValue;

                      setTransferForm((prev) => ({
                        ...prev,
                        quantity: cappedValue,
                      }));
                    }}
                    disabled={isTransferringMaterial}
                    required
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-gray-500">
                    {item?.uom}
                  </span>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 border-t px-5 py-4">
              <button
                type="button"
                className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
                onClick={() => setShowTransferModal(false)}
                disabled={isTransferringMaterial}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="bg-purple-700 text-white px-4 py-2 rounded-lg hover:bg-purple-800 disabled:cursor-not-allowed disabled:opacity-60"
                disabled={
                  isTransferringMaterial ||
                  isLoadingTransferSites ||
                  transferSites.length === 0
                }
              >
                {isTransferringMaterial ? "Transferring..." : "Transfer"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default InventoryItemDetails;
